import { useQuery } from '@tanstack/react-query'
import { Constants } from 'constants/constants'
import { QueryKeys } from 'constants/query-keys'
import { fetchPayrollLocationDailyData } from 'queries/payroll/location/location'
import { PayrollFetchProps } from 'types/api'
import { IResponse, apiNotEnabled } from 'utils/api'
import {
    getGreenfieldDrillValues,
    getGreenfieldLocationFilter,
} from 'utils/greenfield'
import { isStore } from 'utils/location'
import { TIME } from 'utils/misc'
import { useAnalytics } from 'utils/analytics'

interface PayrollLocationDailyFetchProps extends PayrollFetchProps {
    timeDimension?: string
    drillEnabled?: boolean
}

export function usePayrollLocationDaily({
    env,
    accessToken,
    location,
    timeDimension = 'this_month',
    drillEnabled = false,
}: PayrollLocationDailyFetchProps): IResponse {
    const { trackEvent } = useAnalytics()
    const { tenMinutes } = TIME
    const enabled = isStore(location) && drillEnabled
    const greenfieldCardNumber =
        Constants.GREENFIELD_CARDS.PAYROLL.LOCATION_DAILY

    // GF card 539882 does not support time_dimension for current month —
    // omitting the param returns current-month data by default.
    // Only pass time_dimension for other periods (e.g. prior_month).
    const greenfieldFilters: { [key: string]: string } = {
        ...getGreenfieldDrillValues(location),
        ...getGreenfieldLocationFilter(location),
    }
    if (timeDimension && timeDimension !== 'this_month') {
        greenfieldFilters.time_dimension = timeDimension
    }

    const { data, isLoading, isError, refetch, remove, error } = useQuery(
        [QueryKeys.PAYROLL_LOCATION_DAILY, location, timeDimension],
        () =>
            fetchPayrollLocationDailyData({
                env,
                accessToken,
                dynamicSubPath: greenfieldCardNumber,
                filters: greenfieldFilters,
                trackEvent,
            }),
        {
            enabled,
            staleTime: tenMinutes,
            cacheTime: tenMinutes,
            retry: false,
        },
    )

    if (!enabled) {
        return apiNotEnabled()
    }

    return { data, isLoading, isError, refetch, remove, error }
}
